<template>
    <div v-if="visible">
        <h5>
            Purchase Options
            <small class="text-muted">(BuyRx.ai)</small>
        </h5>

        <div v-if="loading" class="text-center my-2">
            <b-spinner small class="align-middle" />
            <strong class="ml-2">Loading purchase options...</strong>
        </div>

        <div v-else-if="loadError" class="alert alert-warning">
            <i class="fa fa-exclamation-triangle" />
            Unable to load BuyRx.ai purchase options at this time.
        </div>

        <template v-else>
            <!-- Average monthly usage / last purchase for this drug -->
            <b-row v-if="primaryLastPurchased" class="py-2">
                <b-col>
                    <b-badge variant="info" class="mr-1">AMU: {{ primaryLastPurchased.amu }}</b-badge>
                    <span v-if="primaryLastPurchased.lastPurchasedDate" class="text-muted">
                        Last purchased {{ formatDate(primaryLastPurchased.lastPurchasedDate) }}
                        @ {{ formatCurrency(primaryLastPurchased.lastPurchasedPrice) }}
                        from {{ primaryLastPurchased.lastPurchasedFrom }}
                        (qty {{ primaryLastPurchased.lastPurchasedQuantity }})
                    </span>
                </b-col>
            </b-row>

            <div v-if="!packageGroups.length" class="text-muted py-2">
                No purchase options found for this drug.
            </div>

            <!-- One expandable card per package size; collapsed shows only the best option -->
            <div v-for="group in packageGroups" :key="group.key" class="border rounded mb-2"
                :class="{ 'border-primary': group.isCurrent }">

                <!-- Card header = package size + expand/collapse control -->
                <div class="d-flex align-items-center px-2 py-1 border-bottom"
                    :style="group.hasAlternates ? 'cursor: pointer' : ''"
                    @click="group.hasAlternates && toggle(group.key)">
                    <i v-if="group.hasAlternates" class="fa mr-2"
                        :class="expanded[group.key] ? 'fa-chevron-down' : 'fa-chevron-right'" />
                    <strong>Package size: {{ group.label }}</strong>
                    <b-badge variant="secondary" class="ml-2">
                        {{ group.all.length }} option{{ group.all.length === 1 ? "" : "s" }}
                    </b-badge>
                    <b-badge v-if="group.isCurrent" variant="primary" class="ml-2">Current package size</b-badge>
                    <small v-if="group.hasAlternates" class="text-muted ml-auto">{{ othersLabel(group) }}</small>
                </div>

                <!-- Collapsed: best only. Expanded: sub-grouped by packageSizeAndDesc. -->
                <div class="px-2 py-1">
                    <div v-for="(item, idx) in visibleItems(group)" :key="item.row.ndc">
                        <div v-if="item.subHeader" class="text-muted small font-weight-bold border-top pt-1 mt-1 mb-1">
                            {{ item.subHeader }}
                        </div>

                        <div :class="idx < visibleItems(group).length - 1 ? 'pb-2 mb-2 border-bottom' : ''">
                            <div class="d-flex align-items-center flex-wrap">
                                <b-badge v-if="item.row === group.best" variant="success" class="mr-2">Best</b-badge>
                                <strong class="mr-2">{{ item.row.drugName }}</strong>
                                <span class="text-muted mr-2">{{ formatNDC(item.row.ndc) }}</span>
                                <span v-if="item.row.mfgName" class="text-muted mr-2">{{ item.row.mfgName }}</span>
                                <b-badge v-if="item.row.brandType" variant="secondary" class="mr-1">{{ item.row.brandType }}</b-badge>
                                <b-badge v-if="item.row.inactive" variant="warning" class="mr-1">Inactive</b-badge>
                                <b-badge v-if="inCartQty[item.row.ndc] > 0" variant="success" class="mr-1">
                                    In Cart ({{ inCartQty[item.row.ndc] }})
                                </b-badge>
                            </div>
                            <div class="text-muted small mb-1">
                                <span v-if="!expanded[group.key] && item.row.packageSizeAndDesc" class="mr-3">
                                    {{ item.row.packageSizeAndDesc }}
                                </span>
                                <span class="mr-3">AWP: {{ formatCurrency(item.row.awp) }}</span>
                                <span class="mr-3">Spread: {{ formatCurrency(item.row.spread) }}</span>
                                <span v-if="item.row.choiceReason">{{ item.row.choiceReason }}</span>
                            </div>

                            <div v-if="item.row.priceList && item.row.priceList.length" class="table-responsive">
                                <table class="table table-sm table-bordered mb-0">
                                    <thead>
                                        <tr>
                                            <th v-for="p in item.row.priceList" :key="p.index"
                                                class="text-nowrap align-middle font-weight-bold"
                                                :class="{ 'table-success': p.selected }">
                                                {{ p.wholesellerName }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td v-for="p in item.row.priceList" :key="p.index"
                                                class="small align-top" :class="{ 'table-success': p.selected }">
                                                <template v-if="p.price > 0">
                                                    <div class="font-weight-bold">{{ formatCurrency(p.price) }}</div>
                                                    <div v-if="p.pricePerPill && p.pricePerPill !== 'N/A'" class="text-body">
                                                        {{ p.pricePerPill }}
                                                    </div>
                                                    <div><b-badge :variant="inventoryVariant(p.inventory)">
                                                        {{ inventoryStatus(p.inventory) }}
                                                    </b-badge></div>
                                                    <div v-if="p.expDate" class="text-body">Exp {{ p.expDate }}</div>
                                                    <div v-if="p.contract"><b-badge variant="info">Contract</b-badge></div>

                                                    <div class="d-flex align-items-center mt-1" style="gap: 4px;">
                                                        <b-form-input v-model.number="quantities[cartKey(item.row.ndc, p.catalogId)]"
                                                            type="number" min="1" step="1" size="sm" class="bg-white" style="width: 60px;" />
                                                        <b-button variant="primary" size="sm"
                                                            :disabled="addingKey === cartKey(item.row.ndc, p.catalogId)"
                                                            @click="addToCart(item.row.ndc, p.catalogId)">
                                                            <b-spinner v-if="addingKey === cartKey(item.row.ndc, p.catalogId)" small />
                                                            Add
                                                        </b-button>
                                                    </div>
                                                </template>
                                                <span v-else class="text-muted">—</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-else class="text-muted small mb-2">No wholesaler prices available.</div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Drug } from "@/models/Drug/Drug";
import moment from "moment";

interface PriceListItem {
    index: number;
    wholesellerName: string;
    catalogId: string;
    price: number;
    pricePerPill: string;
    estNet: number;
    inventory: number;
    expDate: string;
    contract: boolean;
    selected: boolean;
}

interface DrugRow {
    ndc: string;
    drugName: string;
    mfgName: string;
    packageSize?: string;
    packageSizeAndDesc?: string;
    awp: number;
    spread: number;
    brandType: string;
    choiceReason: string;
    inactive: boolean;
    priceList: PriceListItem[];
}

interface LastPurchasedItem {
    ndc: string;
    amu: number;
    lastPurchasedDate: string | null;
    lastPurchasedPrice: number;
    lastPurchasedFrom: string;
    lastPurchasedQuantity: number;
}

/** A row to render inside a package-size card; subHeader is set on the first row of each packageSizeAndDesc sub-group. */
interface DisplayItem {
    row: DrugRow;
    subHeader: string | null;
}

/** A package-size bucket: the platform's chosen "best" drug plus the alternates, sub-grouped by description. */
interface PackageGroup {
    key: string;
    label: string;
    all: DrugRow[];
    best: DrugRow;
    items: DisplayItem[];
    hasAlternates: boolean;
    isCurrent: boolean;
}

@Component({ name: "BuyRxPurchaseOptions" })
export default class BuyRxPurchaseOptions extends Vue {
    @Prop({ required: true }) protected ndc!: string;
    /** Package size of the drug currently loaded on the page; used to surface the matching card first. */
    @Prop({ default: null }) protected packageSize!: number | string | null;

    private configured = false;
    private hasPermission = false;
    private loading = false;
    private loadError = false;

    private drugRows: DrugRow[] = [];
    private lastPurchased: LastPurchasedItem[] = [];
    private quantities: Record<string, number> = {};
    private inCartQty: Record<string, number> = {};
    private cartChecked: Set<string> = new Set();
    private expanded: Record<string, boolean> = {};
    private addingKey: string | null = null;

    get visible(): boolean {
        return this.configured && this.hasPermission;
    }

    get primaryLastPurchased(): LastPurchasedItem | null {
        const target = this.normalizeNdc(this.ndc);
        return this.lastPurchased.find(l => this.normalizeNdc(l.ndc) === target) ?? null;
    }

    private inventoryStatus(inventory: number): string {
        if (inventory === 1) return "In Stock";
        if (inventory === -1) return "Stock Unknown";
        return "Out of Stock";
    }

    private inventoryVariant(inventory: number): string {
        if (inventory === 1) return "success";
        if (inventory === -1) return "secondary";
        return "warning";
    }

    /**
     * Top-level grouping is by package size. Within each package size the drugs are sub-grouped by
     * packageSizeAndDesc, and the "best" drug (the one the platform flagged with a ChoiceReason, or
     * the lowest net price when none/several carry a reason) is chosen across the whole package size.
     * The package size that matches the drug being viewed is surfaced first.
     */
    get packageGroups(): PackageGroup[] {
        const groups: PackageGroup[] = [];
        const byKey: Record<string, PackageGroup> = {};

        for (const row of this.drugRows) {
            const label = (row.packageSize || "").trim() || "Unspecified";
            let group = byKey[label];
            if (!group) {
                group = { key: label, label, all: [], best: row, items: [], hasAlternates: false, isCurrent: false };
                byKey[label] = group;
                groups.push(group);
            }
            group.all.push(row);
        }

        // The card matching the loaded drug's package size is surfaced first; when that size isn't
        // known (or matches nothing) fall back to the package that contains the drug's own NDC.
        const currentSize = this.packageSizeValue(this.packageSize);
        const currentNdc = this.normalizeNdc(this.ndc);
        for (const group of groups) {
            group.best = this.pickBest(group.all);
            group.hasAlternates = group.all.length > 1;
            group.isCurrent = currentSize !== null
                ? this.packageSizeValue(group.label) === currentSize
                : group.all.some(r => this.normalizeNdc(r.ndc) === currentNdc);
            group.items = this.buildSubGroupedItems(group.all);
        }

        // Stable sort keeps first-seen order, but floats the current drug's package size to the top.
        return [...groups].sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent));
    }

    /**
     * Sub-group a package size's drugs by packageSizeAndDesc, cheapest first within each sub-group.
     * Sub-headers are only emitted when a package size actually spans more than one description
     * (otherwise a lone "1 Bottle" header on every card is just noise).
     */
    private buildSubGroupedItems(rows: DrugRow[]): DisplayItem[] {
        const order: string[] = [];
        const byDesc: Record<string, DrugRow[]> = {};

        for (const row of rows) {
            const desc = (row.packageSizeAndDesc || row.packageSize || "").trim() || "Other";
            if (!byDesc[desc]) {
                byDesc[desc] = [];
                order.push(desc);
            }
            byDesc[desc].push(row);
        }

        const showHeaders = order.length > 1;
        const items: DisplayItem[] = [];
        for (const desc of order) {
            byDesc[desc]
                .sort((a, b) => this.bestPrice(a) - this.bestPrice(b))
                .forEach((row, idx) => items.push({ row, subHeader: showHeaders && idx === 0 ? desc : null }));
        }
        return items;
    }

    /** Rows to render for a group: just the best when collapsed, the full sub-grouped list when expanded. */
    private visibleItems(group: PackageGroup): DisplayItem[] {
        return this.expanded[group.key] ? group.items : [{ row: group.best, subHeader: null }];
    }

    private toggle(key: string) {
        const expanding = !this.expanded[key];
        this.$set(this.expanded, key, expanding);
        // Alternates only become visible on expand, so check their cart status now (not before).
        if (expanding) {
            const group = this.packageGroups.find(g => g.key === key);
            if (group) this.ensureCartStatus(group.all);
        }
    }

    private othersLabel(group: PackageGroup): string {
        if (this.expanded[group.key]) return "Hide options";
        const count = group.all.length - 1;
        return `Show ${count} other option${count === 1 ? "" : "s"} in this package size`;
    }

    private pickBest(rows: DrugRow[]): DrugRow {
        // Prefer active drugs, then the platform's flagged choice, then the lowest net price.
        const active = rows.filter(r => !r.inactive);
        const pool0 = active.length ? active : rows;
        const withReason = pool0.filter(r => (r.choiceReason ?? "").trim().length > 0);
        const pool = withReason.length ? withReason : pool0;
        return [...pool].sort((a, b) => this.bestPrice(a) - this.bestPrice(b))[0];
    }

    /** Lowest usable price for a drug (Est. Net preferred, then list price); Infinity when none. */
    private bestPrice(row: DrugRow): number {
        const prices = (row.priceList ?? [])
            .map(p => (p.estNet && p.estNet > 0 ? p.estNet : p.price))
            .filter(v => typeof v === "number" && v > 0);
        return prices.length ? Math.min(...prices) : Number.POSITIVE_INFINITY;
    }

    async mounted() {
        await this.loadStatus();
        if (this.visible) await this.loadOptions();
    }

    @Watch("ndc")
    async onNdcChanged() {
        if (this.visible) await this.loadOptions();
    }

    private async loadStatus() {
        try {
            const { data } = await this.$http.get<{ configured: boolean; hasPermission: boolean }>(
                "/CheckMyRxCost/status");
            this.configured = data.configured;
            this.hasPermission = data.hasPermission;
        } catch {
            // Treat any status failure as "feature unavailable" — the panel simply stays hidden.
            this.configured = false;
        }
    }

    private async loadOptions() {
        if (!this.ndc) return;
        try {
            this.loadError = false;
            this.loading = true;
            this.drugRows = [];
            this.inCartQty = {};
            this.cartChecked = new Set();
            this.expanded = {};

            const { data } = await this.$http.get<{ drugGroups: { drugRows: DrugRow[] }[] }>(
                "/CheckMyRxCost/options", { params: { value: this.normalizeNdc(this.ndc) } });

            this.drugRows = (data.drugGroups ?? []).flatMap(g => g.drugRows ?? []);
            // A separate quantity per wholesaler cell (keyed by NDC + catalogId).
            this.drugRows.forEach(r => {
                (r.priceList ?? []).forEach(p => {
                    if (!p.catalogId) return;
                    const key = this.cartKey(r.ndc, p.catalogId);
                    if (this.quantities[key] === undefined) this.$set(this.quantities, key, 1);
                });
            });

            // Only check cart status for the rows that are visible on load (the best of each package
            // size); a group's alternates are checked lazily when it is expanded.
            await Promise.all([
                this.loadLastPurchased(),
                this.ensureCartStatus(this.packageGroups.map(g => g.best)),
            ]);
        } catch (err: any) {
            this.loadError = true;
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    private async loadLastPurchased() {
        const ndcs = this.drugRows.map(r => this.normalizeNdc(r.ndc)).filter(Boolean);
        if (!ndcs.length) return;
        try {
            const { data } = await this.$http.post<LastPurchasedItem[]>("/CheckMyRxCost/lastpurchased", ndcs);
            this.lastPurchased = data ?? [];
        } catch {
            // Non-fatal: AMU/last-purchase is supplementary information.
            this.lastPurchased = [];
        }
    }

    /** Fetch cart status for the given rows, skipping any NDC already checked (or in flight). */
    private async ensureCartStatus(rows: DrugRow[]) {
        const toFetch = rows.filter(r => r.ndc && !this.cartChecked.has(r.ndc));
        toFetch.forEach(r => this.cartChecked.add(r.ndc));
        await Promise.all(toFetch.map(r => this.fetchCartLine(r.ndc)));
    }

    /** GET the cart line for one NDC and record its quantity (0 when not in the cart). */
    private async fetchCartLine(ndc: string) {
        try {
            const { data } = await this.$http.get<{ quantity: number } | null>(
                `/CheckMyRxCost/cart/${this.normalizeNdc(ndc)}`);
            this.$set(this.inCartQty, ndc, data && data.quantity > 0 ? data.quantity : 0);
        } catch {
            this.$set(this.inCartQty, ndc, 0);
        }
        this.cartChecked.add(ndc);
    }

    /** Cart quantities and in-flight state are tracked per wholesaler cell (NDC + catalogId). */
    private cartKey(ndc: string, catalogId: string | null): string {
        return `${ndc}|${catalogId ?? ""}`;
    }

    private async addToCart(ndc: string, catalogId: string | null) {
        const key = this.cartKey(ndc, catalogId);
        const quantity = this.quantities[key];
        // The add-to-cart route constraint is {quantity:int}; a decimal would 404, so reject it here.
        if (!quantity || quantity <= 0 || !Number.isInteger(quantity)) {
            this.$notification(NotificationOptions.warning("Enter a whole number quantity greater than zero."));
            return;
        }
        try {
            this.addingKey = key;
            const params = catalogId ? { catalogId } : undefined;
            const { data } = await this.$http.post<{ message: string }>(
                `/CheckMyRxCost/cart/${this.normalizeNdc(ndc)}/${quantity}`, null, { params });
            const message = (data?.message ?? "").replace(/^["']+|["']+$/g, "").trim();
            this.$notification(NotificationOptions.success(message || "Added to BuyRx cart."));
            // Re-read the cart line so the badge reflects the platform's authoritative quantity.
            await this.fetchCartLine(ndc);
        } catch (err: any) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.addingKey = null;
        }
    }

    private formatCurrency(value: number | null | undefined): string {
        if (value === null || value === undefined) return "—";
        return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
    }

    private formatDate(value: string | null): string {
        return value ? moment(value).format("MM/DD/YYYY") : "";
    }

    private formatNDC(ndc: string): string {
        return Drug.formatNDC542 ? Drug.formatNDC542(ndc) : ndc;
    }

    /** The platform's API keys everything by the raw 11-digit NDC, without hyphens. */
    private normalizeNdc(ndc: string): string {
        return (ndc ?? "").replace(/[^0-9]/g, "");
    }

    /** Leading numeric value of a package size, comparing the drug's numeric size to the API's "1000 EA" strings. */
    private packageSizeValue(value: number | string | null | undefined): number | null {
        if (value === null || value === undefined || value === "") return null;
        const match = String(value).match(/[\d.]+/);
        return match ? parseFloat(match[0]) : null;
    }
}
</script>
